import React, { useRef, useState } from 'react';
import { X, Download, Loader } from 'lucide-react';
import html2canvas from 'html2canvas';
import QuoteCard from './QuoteCard';

const ShareModal = ({ isOpen, onClose, quote }) => {
  const cardRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!cardRef.current || !quote) return;

    setIsDownloading(true);
    try {
      // Configure html2canvas for high quality
      const canvas = await html2canvas(cardRef.current, {
        scale: 2, // Higher resolution
        useCORS: true,
        backgroundColor: '#f5f1e8',
        logging: false,
        width: 1080,
        height: 1350,
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `wisdom-nexus-quote-${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
        setIsDownloading(false);
      }, 'image/png', 0.95);
    } catch (error) {
      console.error('Error generating image:', error);
      alert('Failed to generate image. Please try again.');
      setIsDownloading(false);
    }
  };

  if (!isOpen || !quote) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative z-10 bg-dark-slate rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-porcelain-white/10">
          <h2 className="text-2xl font-serif text-porcelain-white">
            Share Wisdom Card
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-porcelain-white/10 rounded-lg transition-colors"
          >
            <X size={24} className="text-porcelain-white" />
          </button>
        </div>

        {/* Preview Container */}
        <div className="p-4 md:p-6 flex flex-col items-center overflow-auto">
          {/* Card Preview - Scaled down for display */}
          <div className="mb-6 transform origin-top scale-50 md:scale-50">
            <div ref={cardRef} className="inline-block">
              <QuoteCard quote={quote} />
            </div>
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="inline-flex items-center space-x-3 px-8 py-4 bg-cinnabar-red hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-porcelain-white rounded-lg transition-all transform hover:scale-105 shadow-lg font-medium text-lg"
          >
            {isDownloading ? (
              <>
                <Loader size={20} className="animate-spin" />
                <span>Generating Image...</span>
              </>
            ) : (
              <>
                <Download size={20} />
                <span>Download Image</span>
              </>
            )}
          </button>

          <p className="mt-4 text-sm text-porcelain-white/60 text-center max-w-md">
            The image will be downloaded in high quality (1080x1350px), perfect for sharing on Instagram, Twitter, or other social platforms.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
